import { useEffect, useRef, useState } from "react";
import { Upload, Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useApiBase } from "../context/ApiBaseContext";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import VisibilityPicker from "./VisibilityPicker";

// Notes-only kinds. Anything that changes scene or character
// structure (coloured pages, replacement scene pages, etc.) belongs
// to Script Revisions in Settings, not Notes — they are deliberately
// not offered here so the two systems never share language or intent.
const KINDS = [
    { value: "supervisor_notes", label: "Supervisor notes" },
    { value: "scene_notes", label: "Scene notes" },
    { value: "cast_notes", label: "Cast / character notes" },
    { value: "other", label: "Other" },
];

/**
 * Iter-62 PART 9: upload a change document (PDF/DOCX/image/text) to the
 * project's Changes inventory. Owner-only; scene + character are
 * optional and just enrich the timeline filtering.
 */
export default function ChangeDocumentUploadDialog({
    projectId,
    open,
    onOpenChange,
    scenes = [],
    characters = [],
    presetSceneId = null,
    presetCharacterId = null,
    onUploaded,
}) {
    const apiBase = useApiBase();
    const fileRef = useRef(null);
    const [file, setFile] = useState(null);
    const [label, setLabel] = useState("");
    const [kind, setKind] = useState("supervisor_notes");
    const [sceneId, setSceneId] = useState("");
    const [characterId, setCharacterId] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");
    // Iter-134 — visibility scope. Defaults to "everyone" so calling
    // code's behaviour is unchanged unless the user picks a tighter
    // scope.
    const [visibility, setVisibility] = useState("everyone");

    useEffect(() => {
        if (open) {
            setSceneId(presetSceneId || "");
            setCharacterId(presetCharacterId || "");
        }
    }, [open, presetSceneId, presetCharacterId]);

    const reset = () => {
        setFile(null);
        setLabel("");
        setKind("supervisor_notes");
        setSceneId(presetSceneId || "");
        setCharacterId(presetCharacterId || "");
        setSubmitting(false);
        setResult(null);
        setError("");
        setVisibility("everyone");
        if (fileRef.current) fileRef.current.value = "";
    };

    const submit = async (e) => {
        e?.preventDefault?.();
        if (!file) {
            setError("Pick a file first.");
            return;
        }
        setError("");
        setSubmitting(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            if (label.trim()) fd.append("label", label.trim());
            fd.append("kind", kind);
            if (sceneId) fd.append("scene_id", sceneId);
            if (characterId) fd.append("character_id", characterId);
            if (visibility && visibility !== "everyone") {
                fd.append("visibility", visibility);
            }
            const { data } = await api.post(
                `${apiBase}/${projectId}/change-documents`,
                fd,
                { headers: { "Content-Type": "multipart/form-data" } },
            );
            setResult(data);
            toast.success("Document uploaded");
            if (onUploaded) onUploaded(data);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(v) => {
                if (!submitting) {
                    onOpenChange(v);
                    if (!v) reset();
                }
            }}
        >
            <DialogContent
                data-testid="change-document-upload-dialog"
                className="max-w-md bg-card border-white/10 max-h-[92vh] overflow-y-auto"
            >
                <DialogHeader className="pr-8">
                    <DialogTitle className="font-display text-xl font-bold text-white flex items-center gap-2">
                        <Upload size={16} className="text-primary" />
                        Upload reference document
                    </DialogTitle>
                    <DialogDescription className="text-neutral-400">
                        PDF, DOCX, image or text up to 25 MB.
                    </DialogDescription>
                </DialogHeader>

                {result ? (
                    <div className="space-y-3 mt-2">
                        <div className="rounded-xl border border-emerald-300 bg-emerald-50 dark:border-emerald-500/50 dark:bg-emerald-500/20 p-4 flex items-start gap-3">
                            <CheckCircle2
                                size={18}
                                className="text-emerald-700 dark:text-emerald-200 mt-0.5 shrink-0"
                            />
                            <div>
                                <p
                                    data-testid="change-document-upload-success"
                                    className="text-emerald-900 dark:text-emerald-50 font-semibold"
                                >
                                    {result.document?.filename || "File"}{" "}
                                    uploaded
                                </p>
                                <p className="text-[13px] text-emerald-900 dark:text-emerald-50 mt-1">
                                    {result.document?.scene_number
                                        ? `Linked to Sc ${result.document.scene_number}`
                                        : "No scene reference"}
                                    {result.document?.character_name
                                        ? ` · ${result.document.character_name}`
                                        : ""}
                                    {!result.document?.scene_id &&
                                    !result.document?.character_id
                                        ? " · visible in the global Notes timeline"
                                        : ""}
                                </p>
                            </div>
                        </div>
                        {result.looks_like_script ? (
                            <div
                                data-testid="change-document-upload-script-hint"
                                className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-[12px] text-amber-100 flex items-start gap-2"
                            >
                                <AlertTriangle
                                    size={13}
                                    className="text-amber-300 mt-0.5 shrink-0"
                                />
                                <span>
                                    This looks like a script. Please upload reference material only.
                                </span>
                            </div>
                        ) : null}
                        <div className="flex gap-2">
                            <Button
                                type="button"
                                variant="secondary"
                                data-testid="change-document-upload-another"
                                onClick={reset}
                                className="flex-1 min-tap"
                            >
                                Upload another
                            </Button>
                            <Button
                                type="button"
                                data-testid="change-document-upload-close"
                                onClick={() => {
                                    onOpenChange(false);
                                    reset();
                                }}
                                className="flex-1 min-tap font-semibold"
                            >
                                Done
                            </Button>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={submit} className="space-y-3 mt-2">
                        <div className="rounded-xl border border-white/10 bg-secondary/30 p-3 space-y-2">
                            <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                                File
                            </Label>
                            <input
                                ref={fileRef}
                                type="file"
                                data-testid="change-document-file-input"
                                accept=".pdf,.docx,.txt,.fountain,image/*"
                                onChange={(e) =>
                                    setFile(e.target.files?.[0] || null)
                                }
                                className="block w-full text-sm text-neutral-300 file:mr-3 file:px-3 file:py-1.5 file:rounded-md file:border file:border-white/10 file:bg-secondary file:text-neutral-100 file:text-xs"
                            />
                            {file ? (
                                <p
                                    data-testid="change-document-file-picked"
                                    className="text-[11px] text-neutral-400"
                                >
                                    Selected:{" "}
                                    <span className="text-neutral-100">
                                        {file.name}
                                    </span>{" "}
                                    · {(file.size / 1024).toFixed(1)} KB
                                </p>
                            ) : null}
                        </div>

                        <div className="space-y-1.5">
                            <Label
                                htmlFor="cd-label"
                                className="text-[10px] uppercase tracking-[0.2em] text-neutral-500"
                            >
                                Label (optional)
                            </Label>
                            <Input
                                id="cd-label"
                                data-testid="change-document-label-input"
                                value={label}
                                onChange={(e) => setLabel(e.target.value)}
                                placeholder="Day 12 supervisor sheet"
                                maxLength={140}
                                className="h-11 bg-secondary border-transparent text-base"
                            />
                        </div>

                        <div className="space-y-1.5">
                            <Label
                                htmlFor="cd-kind"
                                className="text-[10px] uppercase tracking-[0.2em] text-neutral-500"
                            >
                                Type
                            </Label>
                            <select
                                id="cd-kind"
                                data-testid="change-document-kind-select"
                                value={kind}
                                onChange={(e) => setKind(e.target.value)}
                                className="w-full h-11 bg-secondary border border-white/10 rounded-md px-3 text-sm text-white"
                            >
                                {KINDS.map((k) => (
                                    <option key={k.value} value={k.value}>
                                        {k.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <label className="text-xs text-neutral-300">
                                <span className="block text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-1">
                                    Scene (optional)
                                </span>
                                <select
                                    data-testid="change-document-scene-select"
                                    value={sceneId}
                                    onChange={(e) =>
                                        setSceneId(e.target.value)
                                    }
                                    className="w-full h-11 bg-secondary border border-white/10 rounded-md px-3 text-sm text-white"
                                >
                                    <option value="">— No scene</option>
                                    {scenes.map((s) => (
                                        <option key={s.id} value={s.id}>
                                            {s.episode
                                                ? `EP ${s.episode} · `
                                                : ""}
                                            Sc {s.scene_number || "—"} —{" "}
                                            {s.heading || ""}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <label className="text-xs text-neutral-300">
                                <span className="block text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-1">
                                    Character (optional)
                                </span>
                                <select
                                    data-testid="change-document-character-select"
                                    value={characterId}
                                    onChange={(e) =>
                                        setCharacterId(e.target.value)
                                    }
                                    className="w-full h-11 bg-secondary border border-white/10 rounded-md px-3 text-sm text-white"
                                >
                                    <option value="">— No character</option>
                                    {characters.map((c) => (
                                        <option key={c.id} value={c.id}>
                                            {c.name}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        </div>

                        {/* Iter-134 → Iter-156B.1 — Visibility picker.
                            Defaults to Project Team so the
                            muscle-memory case is unchanged. Tightened
                            options ("Administrators Only",
                            "Creator + Administrators") reflect the
                            actual permission model so users aren't
                            misled into thinking tight scopes are
                            absolute. */}
                        <VisibilityPicker
                            value={visibility}
                            onChange={setVisibility}
                            testidPrefix="change-document-visibility"
                            label="Visibility"
                        />

                        {error ? (
                            <div
                                data-testid="change-document-upload-error"
                                className="flex items-start gap-2 rounded-md border border-red-500/30 bg-red-500/10 p-2 text-sm text-red-200"
                            >
                                <AlertTriangle
                                    size={13}
                                    className="text-red-300 mt-0.5 shrink-0"
                                />
                                {error}
                            </div>
                        ) : null}

                        <div className="flex gap-2 pt-2">
                            <Button
                                type="button"
                                data-testid="change-document-upload-cancel"
                                variant="secondary"
                                onClick={() => {
                                    onOpenChange(false);
                                    reset();
                                }}
                                disabled={submitting}
                                className="flex-1 min-tap"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                data-testid="change-document-upload-submit"
                                disabled={submitting || !file}
                                className="flex-1 min-tap font-semibold"
                            >
                                {submitting ? (
                                    <Loader2
                                        size={14}
                                        className="animate-spin"
                                    />
                                ) : (
                                    "Upload"
                                )}
                            </Button>
                        </div>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}
